import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

interface OverallRingProps {
  approved: number;
  required: number;
  isComplete: boolean;
}

const RING_SIZE = 128;
const RING_THICKNESS = 6;

/**
 * Big circular progress ring for total course completion.
 *
 * Layers two `CircularProgress` variants: a faint 100% track underneath, and
 * the determinate colored ring on top. Center shows `approved / required`
 * with a "complete" caption when the requirement has been fully met.
 */
const OverallRing: React.FC<OverallRingProps> = ({ approved, required, isComplete }) => {
  const safeRequired = Math.max(1, required);
  const pct = Math.min(100, Math.round((approved / safeRequired) * 100));
  const color = isComplete ? 'success' : 'primary';
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 2,
      }}
      role='progressbar'
      aria-valuemin={0}
      aria-valuemax={required}
      aria-valuenow={approved}
      aria-label={`Course progress: ${approved} of ${required} cases approved`}
    >
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        {/* Both rings are decorative: MUI tags every CircularProgress as a
            progressbar, and the wrapper above already carries the real one. */}
        <CircularProgress
          aria-hidden
          variant='determinate'
          value={100}
          size={RING_SIZE}
          thickness={RING_THICKNESS}
          sx={{ color: 'rgba(255,255,255,0.12)' }}
        />
        <CircularProgress
          aria-hidden
          variant='determinate'
          value={pct}
          size={RING_SIZE}
          thickness={RING_THICKNESS}
          color={color}
          sx={{
            position: 'absolute',
            left: 0,
            // Rounded caps for a polished look.
            '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant='h5'
            component='div'
            sx={{ color: '#fff', fontWeight: 700, lineHeight: 1 }}
          >
            {approved}
            <Typography component='span' sx={{ color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>
              {` / ${required}`}
            </Typography>
          </Typography>
          <Typography
            variant='caption'
            sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.25, letterSpacing: 0.4 }}
          >
            {isComplete ? 'Complete' : 'approved'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default OverallRing;
